import { Button, HookedForm, InputField } from '@app/components';
import { Link } from 'react-router-dom';
import Logo from '@app/assets/vertical-logo.svg?react';
import InfoIcon from '@app/assets/icons/info-pass.svg?react';
import PassResetIcon from '@app/assets/images/pass-reset.png';
import { useMemo, useState } from 'react';
import { useNewPasswordPage } from './NewPassword.hooks';

export const NewPassword = () => {
  const {
    resetPasswordEnabled,
    resetPasswordForm,
    submitNewPassword,
    passwordChangeValid,
    navigate,
  } = useNewPasswordPage();

  const [passwordValue, setPasswordValue] = useState('');

  const { hasNumber, hasSymbol, hasUppercase, hasMinLength, strengthSteps } =
    useMemo(() => {
      const numberRule = /\d/.test(passwordValue);
      const symbolRule = /[^A-Za-z0-9]/.test(passwordValue);
      const uppercaseRule = /[A-Z]/.test(passwordValue);
      const minLengthRule = passwordValue.length >= 8;

      // Calculate strength steps sequentially
      let completedSteps = 0;
      if (numberRule) completedSteps++;
      if (symbolRule) completedSteps++;
      if (uppercaseRule) completedSteps++;
      if (minLengthRule) completedSteps++;

      // Create array where each step is filled if enough requirements are met
      const strengthSteps = [
        completedSteps >= 1, // First bar fills when at least 1 requirement is met
        completedSteps >= 2, // Second bar fills when at least 2 requirements are met
        completedSteps >= 3, // Third bar fills when at least 3 requirements are met
        completedSteps >= 4, // Fourth bar fills when all 4 requirements are met
      ];

      return {
        hasNumber: numberRule,
        hasSymbol: symbolRule,
        hasUppercase: uppercaseRule,
        hasMinLength: minLengthRule,
        strengthSteps,
      };
    }, [passwordValue]);

    return (
    <div className="flex items-center justify-center flex-col min-h-screen py-6">
      {!passwordChangeValid ? (
        <>
          <Logo width={139} height={110} className="mb-12" />
          <div className="bg-white text-center space-y-6 rounded-lg max-w-[510px] px-10 pt-10 pb-6 shadow-sm">
            <div className="space-y-2">
              <h2 className="font-bold text-2xl text-[#1F2937]">Reset Password</h2>
              <p className="text-sm text-[#6B7280]">
                The new password must be different from the old one.
              </p>
            </div>
            <HookedForm
              formHook={resetPasswordForm}
              onSubmit={submitNewPassword}
              className="space-y-4"
            >
              <div className="space-y-2 text-left">
                <label className="block text-sm font-medium text-[#374151]">
                  Set new password
                </label>
                <InputField
                  name="password"
                  type="password"
                  placeholder="8+ characters"
                  externalHandler={(e) => setPasswordValue(e.target.value)}
                  hideErrors
                  className="w-full px-3 py-2 border border-[#D1D5DB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
                />
              </div>

              <div className="flex gap-4 items-center">
                {strengthSteps.map((ok, idx) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={idx}
                    className={`h-2 rounded-full flex-1 ${ok ? 'bg-system-success' : 'bg-base-ghostWhite'}`}
                  />
                ))}
              </div>

              <div className="flex items-center text-[#6B7280] text-xs">
                <InfoIcon className="w-6 h-6 text-[#505563]" />
                <span className="whitespace-nowrap">
                  &nbsp;Must have at least{' '}
                  <span className={hasNumber ? 'line-through' : ''}>
                    1 number
                  </span>
                  {', '}
                  <span className={hasSymbol ? 'line-through' : ''}>
                    1 symbol
                  </span>
                  {', '}
                  <span className={hasUppercase ? 'line-through' : ''}>
                    1 uppercase
                  </span>
                  {' and be '}
                  <span className={hasMinLength ? 'line-through' : ''}>
                    8 characters long
                  </span>
                </span>
              </div>

              <div className="space-y-2 text-left">
                <label className="block text-sm font-medium text-[#374151]">
                  Confirm new password
                </label>
                <InputField
                  name="confirmPassword"
                  type="password"
                  placeholder="8+ characters"
                  hideErrors
                  className="w-full px-3 py-2 border border-[#D1D5DB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B7280] focus:border-transparent"
                />
              </div>

              <Button
                type="submit"
                disabled={!resetPasswordEnabled}
                className="w-full"
              >
                Password Reset
              </Button>
            </HookedForm>
          </div>
          <p className="mt-auto text-base-coolGray">
            Remember password?{' '}
            <Link to="/login" className="text-brand-baseBlue font-bold">
              Log In
            </Link>
          </p>
        </>
      ) : (
        <>
          <Logo width={139} height={110} className="mb-16 mt-2" />
          <div className="bg-white text-center space-y-6 rounded-lg max-w-[510px] px-10 pt-10 pb-6 shadow-sm">
            <div className="flex items-center justify-center mb-6">
              <img 
                src={PassResetIcon} 
                alt="Password reset success" 
                className="w-28 h-28"
              />
            </div>
            <div className="space-y-2">
              <h2 className="font-bold text-2xl text-[#1F2937]">Password Reset</h2>
              <p className="text-sm text-[#6B7280]">
                Your password has been successfully reset. Click below to log in.
              </p>
            </div>
            <Button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Log In
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
